import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import queryString from 'query-string';
import {
  DISMISSED_ITEMS_STORAGE_KEY,
  ENGLISH_LOCALE_CODE,
  isLanguageSymbol,
  LOCALE_QUERY_STRING_KEY,
  MOBILE_MAX_BREAK_NUMBER,
  SELECTED_LOCALE_STORAGE_KEY,
  type LanguageSymbols,
} from '#/shared/constants';
import { type DismissableItemId } from '#/user/constants';
import { getItem } from '#/shared/utils/storage';
import type { State } from '#/user/types';
import type { RootState } from '#/state/store';

const localeQueryParam = queryString.parse(
  typeof window !== 'undefined' ? window.location.search : ''
)[LOCALE_QUERY_STRING_KEY];

// for the initial locale the querystring parameter takes precedence,
// then the local storage value and the fallback is English
export const INITIAL_LOCALE: LanguageSymbols = isLanguageSymbol(
  localeQueryParam
)
  ? localeQueryParam
  : getItem(SELECTED_LOCALE_STORAGE_KEY, ENGLISH_LOCALE_CODE);

const initialState: State = {
  isDialogOpened: false,
  sidePanelIsMinimized: false,
  mobileMode: false,
  alertBannerIsVisible: false,
  globalAlertTimestamp: null,
  selectedLocale: INITIAL_LOCALE,
  dismissedItems: getItem(DISMISSED_ITEMS_STORAGE_KEY, {}),
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setSidePanelIsMinimized(state, action: PayloadAction<boolean>) {
      state.sidePanelIsMinimized = action.payload;
    },
    viewportWidthChanged(state, action: PayloadAction<number>) {
      state.mobileMode = action.payload <= MOBILE_MAX_BREAK_NUMBER;
    },
    setAlertBannerVisibility(state, action: PayloadAction<boolean>) {
      state.alertBannerIsVisible = action.payload;
    },
    setGlobalAlertTimestamp(state, action: PayloadAction<string | null>) {
      state.globalAlertTimestamp = action.payload;
    },
    setDialogOpened(state, action: PayloadAction<boolean>) {
      state.isDialogOpened = action.payload;
    },
    setSelectedLanguage(state, action: PayloadAction<LanguageSymbols>) {
      state.selectedLocale = action.payload;
    },
    dismissItem(state, action: PayloadAction<DismissableItemId>) {
      state.dismissedItems[action.payload] = true;
    },
  },
});

export const {
  setSidePanelIsMinimized,
  viewportWidthChanged,
  setAlertBannerVisibility,
  setGlobalAlertTimestamp,
  setDialogOpened,
  setSelectedLanguage,
  dismissItem,
} = userSlice.actions;
export default userSlice.reducer;

export const selectMobileMode = (state: RootState): boolean =>
  state.user.mobileMode;

export const selectedItemDismissed =
  (itemId: DismissableItemId) =>
  (state: RootState): boolean =>
    !!state.user.dismissedItems[itemId];
